/* eslint-disable no-useless-escape */
import dayjs from 'dayjs';
import { ACCESS_TOKEN_KEY } from './init.constants';
import { toast } from 'sonner';

export const getAuthLocalStorage = () => {
  const authData = localStorage.getItem(ACCESS_TOKEN_KEY);
  return authData && JSON.parse(authData);
};

export const formatMoney = (money) =>
  money.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');

export const parseJwt = (token) => {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch (e) {
    return null;
  }
};

export const getAccessToken = () => {
  const data = getAuthLocalStorage();
  return data?.accessToken || '';
};

export const getRefreshToken = () => {
  const data = getAuthLocalStorage();
  return data?.refreshToken || '';
};

export const isValidPassword = () => {
  const regex = /^\S{6,14}$/;
  return regex;
};

export const isValidEmail = () => {
  const regex = /^[\w.-]+@([\w-]+\.)+[\w-]{2,4}$/;
  return regex;
};

export const isValidPostalCode = () => {
  const regex = /^(?=.*\d).{6,8}$/;
  return regex;
};

export const isRegexSpecialCharacter = () => {
  return /^[a-zA-Z0-9]*$/;
};
export const isRegexSpecialCharacterJP = () => {
  const regex =
    /[\u3040-\u309F\u30A0-\u30FF\uFF66-\uFF9D\u4E00-\u9FFF\u{1F300}-\u{1FAD6}\u{1F600}-\u{1F64F}]/u;
  return regex;
};

export const isREgexCheckIdValid = () => {
  const regex = /^[a-zA-Z0-9-]*$/;
  return regex;
};

export const isRegexFullWidthCharacter = () => {
  const regex = /[\uFF01-\uFF60\uFFE0-\uFFE6]/;
  return regex;
};
export const isPositiveInteger = () => {
  const regex = /^[1-9]\d*$/;
  return regex;
};

export const isValidPhoneNumber = () => {
  const regex = /^\d{10,11}$/;
  return regex;
};

export const isValidURL = () => {
  const regex =
    /^(https?:\/\/)((\[[0-9a-fA-F:.]+\])|((?:\d{1,3}\.){3}\d{1,3})|(?:[a-z0-9-]+\.)+[a-z]{2,})(:\d{2,5})?(\/[^\s?#]*)*(\?[^\s#]*)?(#[^\s]*)?$/i;
  return regex;
};

export const isValidDate = () => {
  const regex =
    /^(?:(\d{4})\/(?:(?:0[13578]|1[02])\/(?:0[1-9]|[12]\d|3[01])|(?:0[469]|11)\/(?:0[1-9]|[12]\d|30)|02\/(?:0[1-9]|1\d|2[0-8])))$|^(?:(?:\d{2}(?:0[48]|[2468][048]|[13579][26])|(?:[02468][048]|[13579][26])00)\/02\/29)$/;
  return regex;
};

export const isZipCodeJP = () => {
  const regex = /^\d{3}-\d{4}$/;
  return regex;
};

// Convenience boolean validator to avoid misuse of the regex function
export const isValidURLString = (value = '') => {
  return isValidURL().test(String(value).trim());
};

export const isMax50Chars = () => {
  const regex = /^.{0,49}$/;
  return regex;
};

export const getBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

export const customLabelChart = {
  id: 'customLabelChart',
  afterDatasetsDraw(chart) {
    const {
      ctx,
      chartArea: { top },
    } = chart;
    ctx.save();
    ctx.font = '14px Arial';
    ctx.fillText('件', 27, top - 20);
    ctx.restore();
  },
};

export const convertStatus = (status) => {
  switch (status) {
    case 'Active':
      return (
        <div className="rounded bg-[#E6F7FF] py-[6px] text-center text-[#1890FF]">
          アクティブ
        </div>
      );

    default:
      return (
        <div className="rounded bg-[#FFF7E6] py-[6px] text-center text-[#FA8C16]">
          非アクティブ
        </div>
      );
  }
};

export const convertTagStatus = (status) => {
  switch (status) {
    case 'WAITING_FOR_PAYMENT':
      return (
        <div className="rounded bg-[#EBFFEE] py-[6px] text-center text-[#34C759]">
          支払い待ち
        </div>
      );
    case 'WAITING_FOR_REGISTRATION':
      return (
        <div className="rounded bg-[#FFF7E6] py-[6px] text-center text-[#FA8C16]">
          キャンセル待ち
        </div>
      );
    case 'REGISTERED':
      return (
        <div className="rounded bg-[#E6F7FF] py-[6px] text-center text-[#1890FF]">
          登録済み
        </div>
      );

    default:
      return (
        <div className="rounded bg-[#F9DEDC] py-[6px] text-center text-[#FF3B30]">
          キャンセル
        </div>
      );
  }
};

export const customizeRequiredMark = (label, { required }) => (
  <div className="flex gap-1">
    {label}
    {required && <span style={{ color: 'red' }}>*</span>}
  </div>
);

export const disabledDate = (current) => {
  // Can not select days before today and today
  return current && current < dayjs().endOf('day');
};

export const disabledDateOfBirth = (current) => {
  return current && current >= dayjs().startOf('day');
};

export const formatDate = (isoString) => {
  const date = new Date(isoString);
  const options = {
    timeZone: 'Asia/Ho_Chi_Minh',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  };

  return new Intl.DateTimeFormat('vi-VN', options).format(date);
};

export const formatDateToYMD = (isoString) => {
  const date = new Date(isoString);

  const vietnamOffset = 7 * 60;
  const localTime = new Date(
    date.getTime() + (vietnamOffset - date.getTimezoneOffset()) * 60000
  );

  const year = localTime.getFullYear();
  const month = String(localTime.getMonth() + 1).padStart(2, '0');
  const day = String(localTime.getDate()).padStart(2, '0');

  return `${year}/${month}/${day}`;
};

export function stripTags(html) {
  const tmp =
    typeof window !== 'undefined' ? document.createElement('div') : null;
  if (tmp) {
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  }
  // fallback server-side simple:
  return html.replace(/<\/?[^>]+(>|$)/g, '');
}

export const isValidMemberId = () => {
  const regex = /^[A-Za-z0-9]{1,12}$/;
  return regex;
};

export const resetForm = (initialFormState) => {
  return Object.fromEntries(
    Object.entries(initialFormState).map(([key, value]) => {
      if (Array.isArray(value)) return [key, []];
      if (key === 'status') return [key, 'Active'];
      return [key, ''];
    })
  );
};

export const convertDateIsoToYMD = (dateString) => {
  if (!dateString) return '';

  const date = new Date(dateString);

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}/${month}/${day}`;
};

export const handleAddImage = ({ label, value, onChange }) => {
  const file = value;
  if (!file) return;

  const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
  const errorMessages = [];

  // Validate type
  if (!allowedTypes.includes(file.type)) {
    errorMessages.push(
      'アップロードできるファイル形式はJPG 、JPEG 、PNGのみです。'
    );
  }

  // Validate size
  const maxSizeInBytes = 10 * 1024 * 1024; // 10MB
  if (file.size > maxSizeInBytes) {
    errorMessages.push('最大アップロードサイズは10MBまでです');
  }

  // Always check dimensions so we can report both size and dimension errors at once
  const reader = new FileReader();
  reader.onload = (e) => {
    const img = new Image();
    img.onload = () => {
      if (img.width > 1920 || img.height > 1080) {
        errorMessages.push('最大1920×1080ピクセル迄、16:9のサイズが最適です。');
      }

      if (errorMessages.length > 0) {
        errorMessages.forEach((m) => toast.error(m));
        return;
      }

      // No errors -> accept file
      onChange({
        [label]: file,
      });
    };
    img.src = e.target.result;
  };
  reader.onerror = () => {
    // If image cannot be read, still report collected non-dimension errors
    if (errorMessages.length > 0) {
      errorMessages.forEach((m) => toast.error(m));
      return;
    }
    toast.error('ファイルの読み込みに失敗しました');
  };
  reader.readAsDataURL(file);
};

export const downloadBase64Image = (base64Data, filename = 'qrCode.jpg') => {
  try {
    const arr = base64Data.split(',');
    const bstr = atob(arr[arr.length - 1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    const blob = new Blob([u8arr], { type: 'image/jpeg' });

    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);

    if (!filename.toLowerCase().endsWith('.jpg')) {
      filename += '.jpg';
    }

    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(link.href);
  } catch (error) {
    return error
  }
};

export const courseListData = [
  {
    value: 'ALL',
    label: '全て',
  },
  {
    value: 'マットⅠⅡ',
    label: 'マットⅠⅡ',
  },
  {
    value: 'プロップス',
    label: 'プロップス',
  },
  {
    value: 'バレル',
    label: 'バレル',
  },
  {
    value: 'チェア',
    label: 'チェア',
  },
  {
    value: 'リフォーマーⅠ',
    label: 'リフォーマーⅠ',
  },
  {
    value: 'リフォーマーⅡ',
    label: 'リフォーマーⅡ',
  },
  {
    value: 'リフォーマーⅢ',
    label: 'リフォーマーⅢ',
  },
  {
    value: 'タワー',
    label: 'タワー',
  },
  {
    value: 'コアアラインⅠ',
    label: 'コアアラインⅠ',
  },
  {
    value: 'コアアラインⅡ',
    label: 'コアアラインⅡ',
  },
  {
    value: 'ユアバック',
    label: 'ユアバック',
  },
  {
    value: 'インテグレーティッド',
    label: 'インテグレーティッド',
  },
];

export const convertToUtc = (time) => {
  const localDate = new Date(time);

  const year = localDate.getUTCFullYear();
  const month = String(localDate.getUTCMonth() + 1).padStart(2, '0');
  const day = String(localDate.getUTCDate()).padStart(2, '0');
  const hours = String(localDate.getUTCHours()).padStart(2, '0');
  const minutes = String(localDate.getUTCMinutes()).padStart(2, '0');

  return `${year}/${month}/${day} ${hours}:${minutes}`;
};

export const convertToLocalTime = (utcTime) => {
  const localDate = new Date(utcTime);

  const year = localDate.getFullYear();
  const month = String(localDate.getMonth() + 1).padStart(2, '0');
  const day = String(localDate.getDate()).padStart(2, '0');
  const hours = String(localDate.getHours()).padStart(2, '0');
  const minutes = String(localDate.getMinutes()).padStart(2, '0');

  return `${year}/${month}/${day} ${hours}:${minutes}`;
};

export const isScheduleValid = (entry, prevEntry) => {
  const format = 'YYYY/MM/DD HH:mm';

  if (!entry?.start || !entry?.end || !entry?.date) {
    return {
      id: entry.id,
      label: 'dateTimeEntries',
      isValid: false,
      message: '開催日時を入力してください。',
    };
  } else if (entry.date && dayjs(entry.start).isAfter(dayjs(entry.end))) {
    return {
      id: entry.id,
      label: 'dateTimeEntries',
      isValid: false,
      message: '開催日時を正しく入力してください。',
    };
  } else if (entry.date && dayjs(entry.start).isSame(dayjs(entry.end))) {
    return {
      id: entry.id,
      label: 'dateTimeEntries',
      isValid: false,
      message: '開催日時を正しく入力してください。',
    };
  } else if (prevEntry.date && prevEntry.start && prevEntry.end) {
    if (
      prevEntry.date === entry.date ||
      !dayjs(prevEntry.date).isBefore(entry.date)
    ) {
      return {
        id: entry.id,
        label: 'dateTimeEntries',
        isValid: false,
        message: '開催日時を正しく入力してください。',
      };
    }
  }

  const startTime = dayjs.isDayjs(entry.start)
    ? entry.start
    : dayjs(entry.start, format, true);

  const endTime = dayjs.isDayjs(entry.end)
    ? entry.end
    : dayjs(entry.end, format, true);

  if (!startTime.isValid() || !endTime.isValid()) {
    return {
      id: entry.id,
      label: 'dateTimeEntries',
      isValid: false,
      message: '開催日時を入力してください。',
    };
  }

  if (dayjs(startTime).isAfter(dayjs(endTime))) {
    return {
      id: entry.id,
      label: 'dateTimeEntries',
      isValid: false,
      message: '開催日時を正しく入力してください。',
    };
  }
};

export const getDateMinus = (date, subDay) => {
  const newDate = dayjs(date).subtract(subDay, 'day');
  return newDate.format('YYYY/MM/DD');
};

export const mergeErrors = (...errorArrays) => {
  return errorArrays
    .flat(10)
    .filter(Boolean)
    .reduce((acc, current) => {
      if (!current || typeof current !== 'object') return acc;

      const key = `${current.id || 'no-id'}-${current.label}`;
      const existingIndex = acc.findIndex(
        (err) => `${err.id || 'no-id'}-${err.label}` === key
      );

      if (existingIndex >= 0) {
        acc[existingIndex] = current;
      } else {
        acc.push(current);
      }

      return acc;
    }, []);
};

export const isHtmlContentEmpty = (html) => {
  if (!html) return true;

  // remove <br>, <br/>, <br />
  const withoutBr = html.replace(/<br\s*\/?>/gi, '');

  // remove all html tags
  const text = withoutBr.replace(/<\/?[^>]+(>|$)/g, '');

  return text.trim().length === 0;
};
